import React, { FC, memo, useState, useEffect, useMemo } from 'react';
import {
  CircularProgress,
  TableCell,
  TextField,
  Popper,
  makeStyles,
  Typography
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import useQuotationApi from 'src/hooks/quotation-v2/use-quotation-api';
import { QuotationCategory } from 'src/types/quotation'; // adjust import path if needed
import useQuotationProduct from 'src/hooks/quotation-v2/use-quotation-products';
import { initialQuotationState } from 'src/constants/quotation';
import { uniq } from 'lodash';

const useStyles = makeStyles(() => ({
  popper: {
    minWidth: 300,
    maxHeight: 400,
    zIndex: 1300,
    borderRadius: 8,
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    backgroundColor: '#fff'
  }
}));

const CustomPopper = (props: any) => {
  const classes = useStyles();
  return <Popper {...props} className={classes.popper} />;
};

interface Props {
  index: number;
  category?: QuotationCategory;
  isScreenCapture: boolean;
}

const Component: FC<Props> = ({ index, category, isScreenCapture = false }) => {
  const {
    is_category_loading,
    categoryOptions,
    fetchQuotationProducts
  } = useQuotationApi();
  const {
    quotationData,
    quotationParams,
    onChangeQuotationCategoryRow
  } = useQuotationProduct();

  const [value, setValue] = useState<QuotationCategory | null>(null);

  const isCustom = useMemo(() => quotationData?.[index]?.custom === true, [
    index,
    quotationData
  ]);

  const onHandleChangeCategory = (value?: string | QuotationCategory) => {
    const currentInitialCategoryState = initialQuotationState?.map(
      (quotation) => quotation?.product?.category_id
    );

    const currentCategoryQuotation = quotationData?.map(
      (quotation) => quotation?.product?.category_id
    );

    const combinedCategory = uniq([
      ...currentInitialCategoryState,
      ...currentCategoryQuotation
    ]);

    let valueToSet: QuotationCategory;
    if (typeof value === 'string') {
      // custom category
      valueToSet = {
        category_id: 0,
        category_name: value
      };
    } else {
      // selected options or cleared
      valueToSet = value || { category_id: 0, category_name: '' };
    }
    setValue(valueToSet || {});

    const categoryAlreadyExist = combinedCategory?.some(
      (category) => category === valueToSet?.category_id
    );

    //refetch category product options
    if (!categoryAlreadyExist && valueToSet?.category_id) {
      const category_id = valueToSet?.category_id;
      fetchQuotationProducts({
        category_ids: category_id ? [category_id] : [],
        branch_id: quotationParams?.branch_id,
        page: 1
      });
    }

    onChangeQuotationCategoryRow(
      index,
      valueToSet || {},
      category || {},
      quotationData
    );
  };

  const getCategoryDisplayName = (categoryName?: string) => {
    const CATEGORY_NAME_MAP: { [key: string]: string } = {
      Motherboard: 'MOBO',
      'CPU Cooler': 'COOLER'
    };
    if (!categoryName) return '';
    return CATEGORY_NAME_MAP[categoryName] || categoryName;
  };

  useEffect(() => {
    if (category && categoryOptions) {
      const match = categoryOptions.find(
        (options) => options.category_id === category.category_id
      );

      if (match) {
        setValue(match);
      } else {
        setValue(category);
      }
    }
  }, [category, categoryOptions]);

  return (
    <TableCell style={{ padding: 0, minWidth: '60px', maxWidth: '60px' }}>
      {isScreenCapture ? (
        <div>
          <Typography>
            {getCategoryDisplayName(value?.category_name)}
          </Typography>
        </div>
      ) : (
        <Autocomplete
          style={{ paddingBottom: '11px' }}
          fullWidth
          disabled={value?.category_name === 'VOUCHER'}
          freeSolo={isCustom}
          PopperComponent={CustomPopper}
          value={getCategoryDisplayName(value?.category_name)}
          onChange={(_, newValue) => onHandleChangeCategory(newValue || {})} // only selection
          options={categoryOptions || []}
          getOptionLabel={(option) =>
            typeof option === 'string' ? option : option.category_name || ''
          }
          loading={is_category_loading}
          openOnFocus
          onInputChange={(_, inputValue, reason) => {
            if (isCustom && reason === 'input') {
              onHandleChangeCategory(inputValue); // only for freeSolo rows
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              multiline
              minRows={1}
              maxRows={3}
              style={{
                width: '10px',
                padding: 0,
                margin: 0
              }}
              InputProps={{
                style: {
                  width: '120px',
                  whiteSpace: 'normal',
                  wordBreak: 'break-word'
                },
                ...params.InputProps,
                disableUnderline: true,
                endAdornment: (
                  <>
                    {is_category_loading && (
                      <CircularProgress color="inherit" size={20} />
                    )}
                  </>
                )
              }}
            />
          )}
        />
      )}
    </TableCell>
  );
};

export const QuotationTypeCell = memo(Component);
